import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import Modal from "../components/common/Modal";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { asArray, unwrapData } from "../lib/catalog";

const CATEGORY_OPTIONS = [
  { value: "booking", label: "Booking" },
  { value: "payment", label: "Payment" },
  { value: "documents", label: "Documents" },
  { value: "visa", label: "Visa" },
  { value: "travel", label: "Travel" },
  { value: "account", label: "Account" },
  { value: "other", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const emptyForm = {
  subject: "",
  description: "",
  category: "booking",
  priority: "medium",
  bookingId: "",
};

export default function SupportTicketsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  async function loadTickets() {
    const payload = await agentApi.get("/travel-partner/support-tickets", {
      params: { page: 1, limit: 50, status: status || undefined },
    });
    const data = unwrapData(payload);
    setRows(asArray(data));
  }

  useEffect(() => {
    let alive = true;
    loadTickets()
      .catch((error) => {
        if (alive) showApiError(error, "LOAD_SUPPORT_TICKETS");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [status]);

  async function createTicket() {
    if (!form.subject.trim() || !form.description.trim()) {
      showErrorAction("TICKET_REQUIRED");
      return;
    }
    try {
      setSaving(true);
      await agentApi.post("/travel-partner/support-tickets", { body: form });
      setOpen(false);
      setForm(emptyForm);
      showSuccessAction("CREATE_SUPPORT_TICKET");
      await loadTickets();
    } catch (error) {
      showApiError(error, "CREATE_SUPPORT_TICKET");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Support"
          title="Support tickets"
          description="Create and track agency tickets. Notifications stay on a separate page."
          items={[DASHBOARD_CRUMB, { label: "Support" }]}
          actions={
            <div className="page-toolbar">
              <AgentSelect
                className="page-search"
                value={status}
                options={[
                  { value: "open", label: "Open" },
                  { value: "in_progress", label: "In progress" },
                  { value: "waiting_for_agent", label: "Waiting for agent" },
                  { value: "resolved", label: "Resolved" },
                  { value: "closed", label: "Closed" },
                ]}
                onChange={setStatus}
                placeholder="All statuses"
                isClearable
              />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
                New ticket
              </button>
            </div>
          }
        />

        {loading ? <PageLoader message="Loading tickets..." /> : null}

        {!loading ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <Table>
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Subject</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? rows.map((row) => (
                    <tr key={row.ticket_id}>
                      <td>
                        <Link to={`/support/${row.ticket_id}`}>{row.ticket_number}</Link>
                      </td>
                      <td>{row.subject}</td>
                      <td><StatusBadge value={row.priority} /></td>
                      <td><StatusBadge value={row.status} /></td>
                      <td>{row.updated_date ? new Date(row.updated_date).toLocaleString("en-GB") : "-"}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center text-secondary py-4">No support tickets yet.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : null}
      </div>

      <Modal
        open={open}
        title="Create support ticket"
        onClose={() => setOpen(false)}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => setOpen(false)}>Close</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={createTicket}>
              {saving ? "Creating..." : "Create ticket"}
            </button>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Subject</label>
            <input className="form-control" value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Category</label>
            <AgentSelect value={form.category} options={CATEGORY_OPTIONS} onChange={(value) => setForm({ ...form, category: value })} isSearchable={false} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Priority</label>
            <AgentSelect value={form.priority} options={PRIORITY_OPTIONS} onChange={(value) => setForm({ ...form, priority: value })} isSearchable={false} />
          </div>
          <div className="col-12">
            <label className="form-label">Booking ID (optional)</label>
            <input className="form-control" value={form.bookingId} onChange={(event) => setForm({ ...form, bookingId: event.target.value })} />
          </div>
          <div className="col-12">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={5} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </div>
        </div>
      </Modal>
    </AgentLayout>
  );
}
